import { ReactNode, useEffect, useState } from "react";
import { ConfigProvider } from "antd";
import type { Locale } from "antd/es/locale";
import enGB from "antd/locale/en_GB";
import idID from "antd/locale/id_ID";
import i18n, { Lang, currentLang } from "i18n";

// en_GB rather than en_US: its bundled-dayjs locale ("en-gb") has weekStart 1,
// so AntD's DatePicker/Calendar keep Monday-first weeks in English without
// patching antd's own dayjs instance, and its DD/MM/YYYY format matches
// DEFAULT_DATE_FORMAT.
export const antdLocaleFor = (lang: Lang): Locale => (lang === "en" ? enGB : idID);

/**
 * Remounts the entire tree when the language changes (key={lang}) so the
 * ~2,100 bare translate() call sites re-resolve; translate is non-reactive
 * i18next.t, so changeLanguage alone repaints nothing. Must sit INSIDE
 * QueryClientProvider so the react-query cache survives the remount. The
 * locale must be derived from state here, not written inline in index.js:
 * root.render() builds its element tree exactly once, so an inline expression
 * there would be frozen forever.
 */
const LanguageRoot = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(currentLang());

  useEffect(() => {
    const onChange = () => setLangState(currentLang());
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, []);

  return (
    <ConfigProvider key={lang} locale={antdLocaleFor(lang)}>
      {children}
    </ConfigProvider>
  );
};

export default LanguageRoot;
